import _, {  } from "lodash"
import { Coordinate, equals } from "ol/coordinate"
import LineString from "ol/geom/LineString"
import { getLength } from "ol/sphere"
import { Renderer } from "./Renderer";
import { createOrUpdateFromCoordinates } from "ol/extent";

export interface SplitInfo {
    firstTrackGeometry: LineString;
    secondTrackGeometry: LineString;
}

export interface ClosestPointInfo {
    intersectionPoint: Coordinate;
    proximityToPassedPoint: number;
    dist: number;
    index: number;
}

export default class SplitHelper {
    public static getIndexOfPointPreceeding(trackLine: LineString, coord: Coordinate): number {
        let indexOfPointPreceeding = -1
        let thisIndex = -1
        let closestDistance: number|undefined = undefined
        trackLine.forEachSegment((c1, c2) => {
            thisIndex++
            const segment = new LineString([c1,c2])
            const closestPointOnThisSegment = segment.getClosestPoint(coord)
            const thisDist = getLength(new LineString([closestPointOnThisSegment, coord]))
            if(closestDistance === undefined || thisDist < closestDistance) {
                closestDistance = thisDist
                indexOfPointPreceeding = thisIndex
            }
        })
        return indexOfPointPreceeding
    }

    public static getPointsOnSegmentNear(trackLine: LineString, coord: Coordinate, renderer: Renderer<any>): ClosestPointInfo[] {
        const tolerance = 8; // radius. it's actually a square, but this won't be a massive user experience issue.
        const pixel = renderer.map.getPixelFromCoordinate(coord)
        const viewport = renderer.map.getViewport()
        
        const pixelExtent = [
            Math.max(pixel[0] - tolerance, 0),
            Math.max(pixel[1] - tolerance, 0),
            Math.min(pixel[0] + tolerance, viewport.clientWidth),
            Math.min(pixel[1] + tolerance, viewport.clientHeight)
        ]
        const coordExtent = createOrUpdateFromCoordinates([
            renderer.map.getCoordinateFromPixel([pixelExtent[0], pixelExtent[1]]),
            renderer.map.getCoordinateFromPixel([pixelExtent[2], pixelExtent[3]])
        ])
        const intersectingPoints: ClosestPointInfo[] = []
        let thisIndex = -1;
        let distUpToThisPoint = 0;
        trackLine.forEachSegment((c1, c2) => {
            thisIndex++;
            const segment = new LineString([c1, c2])
            if(segment.intersectsExtent(coordExtent)) {
                const intersectionPoint = segment.getClosestPoint(coord)
                const proximityToPassedPoint = getLength(new LineString([intersectionPoint, coord]))
                intersectingPoints.push({
                    dist: distUpToThisPoint + getLength(new LineString([c2, intersectionPoint]), {projection: renderer.projection}),
                    index: thisIndex,
                    intersectionPoint: intersectionPoint,
                    proximityToPassedPoint: proximityToPassedPoint
                });
            }
            distUpToThisPoint += getLength(segment, {projection: renderer.projection})
        })
        // group them by contigiousness, so e.g. if we have 140,141,142,2000,2001,2002, we will have [140,141,142], [2000,2001,2002]
        const groups: ClosestPointInfo[][] = []
        let currentGroup: ClosestPointInfo[] = []
        let indexOfLast: number|undefined = undefined;
        intersectingPoints.forEach(intersectingPoint => {
            if(intersectingPoint.index - 1 !== indexOfLast) {
                currentGroup = []
                groups.push(currentGroup)
            }
            currentGroup.push(intersectingPoint)
            indexOfLast = intersectingPoint.index;
        })
        const nearestOfEachGroup = groups.map(g => _.minBy(g, p => p.proximityToPassedPoint)!)
        return nearestOfEachGroup
    }

    public static splitLineAt(trackLine: LineString, splitAt: Coordinate): SplitInfo {
        const allCoords = trackLine.getCoordinates()
        const nearestPointOnLineToEvent = trackLine.getClosestPoint(splitAt)
        const indexOfPointPreceeding = SplitHelper.getIndexOfPointPreceeding(trackLine, nearestPointOnLineToEvent)

        const firstTrackCoords = _.take(allCoords, indexOfPointPreceeding + 1)
        const secondTrackCoords = _.drop(allCoords, indexOfPointPreceeding + 1)
        const firstTrackGeometry = new LineString(firstTrackCoords.concat([nearestPointOnLineToEvent]))
        const secondTrackGeometry = new LineString([nearestPointOnLineToEvent].concat(secondTrackCoords))
        if(!equals(firstTrackGeometry.getLastCoordinate(), secondTrackGeometry.getFirstCoordinate())) {
            console.error('SplitHelper.splitLineAt: first track last coordinate must match second track first coordinate', 
                firstTrackGeometry.getLastCoordinate(), 
                secondTrackGeometry.getFirstCoordinate())
        }
        return { firstTrackGeometry, secondTrackGeometry }
    }
}